import { toaster } from 'components'
import { useWorkspace } from 'components/providers'
import { generatePath, useNavigate } from 'react-router'
import { LimitedResource, SubscriptionPlan } from 'types'
import { usePermission } from './workspaceUtils'

export const resourceLimitsByPlan: Record<SubscriptionPlan, Record<LimitedResource, number>> = {
  basic: {
    events: 3,
    facilities: 1,
    templates: 1,
    users: 3,
  },
  pro: {
    events: 10,
    facilities: 3,
    templates: 3,
    users: 10,
  },
  enterprise: {
    events: Infinity,
    facilities: Infinity,
    templates: Infinity,
    users: Infinity,
  },
}

export type Limitation = 'locked' | 'maxed'

export function useResourceLimitation(resource: LimitedResource): Limitation | undefined {
  const { userCount, subscription } = useWorkspace()

  if (userCount == null || !subscription) return

  const { addOnByResource, countByResource, plan, selectedResource } = subscription
  if (plan === 'basic') {
    if (resource === 'events' && selectedResource === 'facility') return 'locked'
    if (resource === 'facilities' && selectedResource === 'event') return 'locked'
  }

  const limitByResource = resourceLimitsByPlan[plan]
  const getLimit = (resource: LimitedResource) =>
    limitByResource[resource] + (addOnByResource?.[resource] ?? 0)

  if (resource === 'users') return userCount >= getLimit('users') ? 'maxed' : undefined
  if (countByResource[resource] >= getLimit(resource)) return 'maxed'
}

export function useBlockResourceCreation(resource: LimitedResource) {
  const navigate = useNavigate()
  const { workspace } = useWorkspace()
  const permission = usePermission()
  const limitation = useResourceLimitation(resource)

  function blockResourceCreation() {
    if (!limitation) return false

    const isAdmin = permission.workspace

    toaster.create({
      title: `You${
        limitation === 'maxed' ? ' have reached your limit for ' : ' do not have access to '
      }${resource}`,
      description: `Please${
        isAdmin ? '' : ' contact your Momentify Admin to'
      } upgrade your account${
        limitation === 'maxed' ? ` or remove current ${resource} to add more.` : ''
      }`,
      type: 'error',
      action: isAdmin
        ? {
            label: 'Manage Subscription',
            onClick: () =>
              navigate({
                pathname: generatePath(subscriptionPath, { workspaceId: workspace!.id }),
              }),
          }
        : undefined,
    })

    return true
  }

  return blockResourceCreation
}

const subscriptionPath = '/:workspaceId/subscription'

export function useLimitedResourceCounts() {
  let limitCounts: number | undefined
  let warningCounts: number | undefined

  const { subscription, userCount } = useWorkspace()
  if (!subscription || userCount == null) return { limitCounts, warningCounts }

  const { addOnByResource, countByResource, plan } = subscription
  const limitByResource = resourceLimitsByPlan[plan]
  const getLimit = (resource: LimitedResource) =>
    limitByResource[resource] + (addOnByResource?.[resource] ?? 0)

  limitCounts = 0
  warningCounts = 0

  if (userCount >= getLimit('users')) limitCounts++
  else if (userCount >= getLimit('users') - 1) warningCounts++

  if (countByResource.events >= getLimit('events')) limitCounts++
  else if (countByResource.events >= getLimit('events') - 1) warningCounts++

  if (countByResource.facilities >= getLimit('facilities')) limitCounts++
  else if (countByResource.facilities >= getLimit('facilities') - 1) warningCounts++

  if (countByResource.templates >= getLimit('templates')) limitCounts++
  else if (countByResource.templates >= getLimit('templates') - 1) warningCounts++

  return { limitCounts, warningCounts }
}
